import React, { useMemo, useRef } from 'react'
import { Select, Tooltip } from 'antd'
import { connect } from 'react-redux'
import { IconInfo, IconSearch } from '@edulastic/icons'
import { debounce, identity, pickBy } from 'lodash'
import { SelectInputStyled } from '@edulastic/common'
import { roleuser, assignmentStatusOptions } from '@edulastic/constants'
import styled from 'styled-components'
import {
  accessibilityColors,
  mobileWidthLarge,
  themeColorBlue,
} from '@edulastic/colors'
import {
  getCurrentTerm,
  getUserOrgId,
  getUserRole,
} from '../../../src/selectors/user'
import {
  getsearchedAssigmentsListSelector,
  getTestListLoadingSelector,
} from '../../../src/selectors/assignments'
import {
  recieveAssignmentsListAction,
  receiveAssignmentsAction,
  receiveAssignmentsSummaryAction,
  recieveAssignmentsListSuccessAction,
} from '../../../src/actions/assignments'
import { OtherVersionTooltipText, shortTestIdKeyLength } from '../../constants'
import { setSelectSearchAriaLabel } from '../../../../common/utils/helpers'
import {
  optionSeparatorProps,
  otherVersionProps,
  getOptionTitleStyle,
} from './styled'

const { DONE, IN_GRADING, IN_PROGRESS, NOT_OPEN } = assignmentStatusOptions
const showAllValue = 'Show All'

const AssignmentsSearch = ({
  filterState = {},
  userRole,
  onSetFilter,
  loadAssignmentsSummary,
  loadAssignments,
  districtId,
  testsList,
  recieveAssignmentsList,
  testListLoading,
  populateSearchResult = true,
  termId,
  setAssignmentsList,
  labelId,
}) => {
  const searchTextRef = useRef('')

  const handleChange = (value) => {
    if (!populateSearchResult) return
    let filters = {
      ...filterState,
      testId: value === showAllValue ? '' : value,
    }

    if (userRole === roleuser.TEACHER) {
      loadAssignments({ filters, folderId: filters.folderId })
    } else {
      filters = { ...filters, pageNo: 1 }
      loadAssignmentsSummary({
        folderId: filters.folderId,
        districtId,
        filters: pickBy(filters, identity),
        filtering: true,
      })
    }
    onSetFilter(filters)
  }

  const handleSelectAssignment = (value) => {
    if (populateSearchResult) {
      if (value === showAllValue) {
        setAssignmentsList([])
        searchTextRef.current = ''
      }
      return
    }
    onSetFilter(value)
  }

  const query = useMemo(() => {
    return {
      limit: 35,
      search: {
        classSubjects: filterState.subject ? [filterState.subject] : [],
        classGrades: filterState.grades,
        termId: !populateSearchResult ? termId : filterState.termId,
        groupIds: filterState.classId ? [filterState.classId] : [],
        testTypes: filterState.testTypes,
        folderId: filterState.folderId,
        assignedBy: filterState.assignedBy,
        tagIds: filterState.tags || [],
        statuses: filterState.status
          ? [filterState.status]
          : [NOT_OPEN, IN_PROGRESS, IN_GRADING, DONE],
        searchString: filterState.testId,
      },
    }
  }, [filterState])

  const handleSearch = (value = '') => {
    searchTextRef.current = value
    if (value.length < 2) {
      setAssignmentsList([])
      return
    }
    recieveAssignmentsList({
      ...query,
      search: {
        ...query.search,
        searchString: value || '',
      },
    })
  }

  const onSearch = debounce(handleSearch, 500)

  const onDropdownVisibleChange = (open) => {
    if (open) {
      setAssignmentsList([])
    }
  }

  const value =
    !filterState.testId || filterState.testId === showAllValue
      ? undefined
      : filterState.testId

  return (
    <GroupSelectAndIcon
      data-joyride-id="assignmentsSearch"
      populateSearchResult={populateSearchResult}
    >
      <SearchIcon populateSearchResult={populateSearchResult}>
        <IconSearch
          color={
            filterState.testId
              ? themeColorBlue
              : accessibilityColors.selectDropdown.placeholder
          }
        />
      </SearchIcon>
      <div id="search-description" status="status" style={{ display: 'none' }}>
        On entering data into the following input field, the content will update
        below
      </div>
      <StyledSelectInput
        data-cy="filter-test-name"
        mode="default"
        style={{
          width: 241,
          height: populateSearchResult ? 32 : 36,
        }}
        populateSearchResult={populateSearchResult}
        height="32px"
        $paddingRight="0px"
        placeholder={
          !populateSearchResult ? 'Search "Assignments"' : 'Show All'
        }
        aria-label="Search Assignments"
        dropdownClassName="wider-dropdown"
        value={value}
        onChange={handleChange}
        onSelect={handleSelectAssignment}
        onFocus={handleSearch}
        onDropdownVisibleChange={onDropdownVisibleChange}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        filterOption={false}
        loading={testListLoading}
        onSearch={onSearch}
        testSelected={filterState.testId}
        notFoundContent={
          testListLoading
            ? 'Loading...'
            : searchTextRef.current.length < 2
            ? 'Type 2 letters to search'
            : 'No Results'
        }
        showSearch
        ref={(node) => {
          setSelectSearchAriaLabel(node, 'Search Assignments')
        }}
        aria-describedby="search-description"
        aria-labelledby={labelId}
      >
        {value && populateSearchResult && (
          <Select.Option key={0} value={showAllValue}>
            Show All
          </Select.Option>
        )}
        {testsList?.map(
          ({ _id, title, hasChildren, isChild, tooltipInfo }, index, arr) => {
            const elements = []
            // Main option
            elements.push(
              <Select.Option
                title={`${title} ${tooltipInfo || ''}`}
                key={_id}
                value={_id}
              >
                <span
                  className="assignment-option-title"
                  style={getOptionTitleStyle(isChild, false)}
                >
                  {title}
                </span>
                <span
                  style={{ minWidth: '80px', fontWeight: isChild ? 500 : 600 }}
                >{` (ID: ${_id.slice(-shortTestIdKeyLength)})`}</span>
              </Select.Option>
            )

            if (hasChildren) {
              elements.push(
                <Select.Option
                  key={`label-${_id}`}
                  disabled
                  value={`label-${_id}`}
                  style={otherVersionProps}
                >
                  <span>
                    OTHER VERSIONS
                    <Tooltip title={OtherVersionTooltipText}>
                      <IconInfo
                        height={14}
                        width={14}
                        style={{ margin: '0 0 -2px 4px' }}
                      />
                    </Tooltip>
                  </span>
                </Select.Option>
              )
            }
            // Add disabled separator after last child
            const next = arr[index + 1]
            if (isChild && (!next || !next.isChild)) {
              elements.push(
                <Select.Option
                  key={`separator-${_id}`}
                  disabled
                  value={`separator-${_id}`}
                  style={optionSeparatorProps}
                >
                  <div style={{ height: '1px' }} />
                </Select.Option>
              )
            }

            return elements
          }
        )}
      </StyledSelectInput>
    </GroupSelectAndIcon>
  )
}

export default connect(
  (state) => ({
    userRole: getUserRole(state),
    districtId: getUserOrgId(state),
    testsList: getsearchedAssigmentsListSelector(state),
    testListLoading: getTestListLoadingSelector(state),
    termId: getCurrentTerm(state),
  }),
  {
    loadAssignments: receiveAssignmentsAction,
    loadAssignmentsSummary: receiveAssignmentsSummaryAction,
    recieveAssignmentsList: recieveAssignmentsListAction,
    setAssignmentsList: recieveAssignmentsListSuccessAction,
  }
)(AssignmentsSearch)

const StyledSelectInput = styled(SelectInputStyled)`
  color: ${({ populateSearchResult }) =>
    populateSearchResult ? 'auto' : 'black'};
  font-weight: ${({ populateSearchResult }) =>
    populateSearchResult ? 'auto' : '400'};
  &.ant-select {
    .ant-select-selection {
      &.ant-select-selection--single {
        padding-left: ${({ populateSearchResult }) =>
          populateSearchResult ? '20px' : '0px'};
        ${(props) =>
          props.testSelected ? `border: 1px solid ${themeColorBlue};` : ''}
        .ant-select-arrow {
          display: none;
        }
        .ant-select-selection__rendered {
          padding: 0px 10px 0px 8px;
          .ant-select-selection-selected-value {
            .assignment-option-title {
              display: unset !important;
              -webkit-line-clamp: unset !important;
            }
            ${(props) =>
              props.testSelected ? `color: ${themeColorBlue};` : ''}
          }
          .ant-select-selection__placeholder {
            margin-left: 10px;
          }
        }
      }
    }
    @media (max-width: ${mobileWidthLarge}) {
      width: 120px !important;
  }
`

const GroupSelectAndIcon = styled.div`
  position: relative;
  display: inline-block;
  height: ${({ populateSearchResult }) =>
    populateSearchResult ? 'auto' : '36px'};
  .wider-dropdown {
    width: 320px !important;
  }
`
const SearchIcon = styled.span`
  position: absolute;
  display: ${({ populateSearchResult }) =>
    populateSearchResult ? 'inline-block' : 'none'};
  width: 25px;
  z-index: 1;
  top: ${({ populateSearchResult }) => (populateSearchResult ? '8px' : '10px')};
  left: 8px;
`
